// First-level administrative subdivisions (states / provinces / territories)
// for the countries where we offer a structured dropdown. Countries not listed
// here fall back to a free-text state/province input (see getSubdivisions).
//
// Codes are the official ISO 3166-2 subdivision codes without the country
// prefix (e.g. "CA" for California, "ON" for Ontario), which is how US/CA/AU
// postal addresses are conventionally written.

export interface Subdivision {
  /** Subdivision code, e.g. "CA", "ON", "NSW". */
  code: string;
  /** Full name, e.g. "California". */
  name: string;
}

export const US_STATES: readonly Subdivision[] = [
  { code: "AL", name: "Alabama" },
  { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" },
  { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" },
  { code: "DE", name: "Delaware" },
  { code: "DC", name: "District of Columbia" },
  { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" },
  { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" },
  { code: "WY", name: "Wyoming" },
  { code: "AS", name: "American Samoa" },
  { code: "GU", name: "Guam" },
  { code: "MP", name: "Northern Mariana Islands" },
  { code: "PR", name: "Puerto Rico" },
  { code: "VI", name: "U.S. Virgin Islands" },
];

export const CA_PROVINCES: readonly Subdivision[] = [
  { code: "AB", name: "Alberta" },
  { code: "BC", name: "British Columbia" },
  { code: "MB", name: "Manitoba" },
  { code: "NB", name: "New Brunswick" },
  { code: "NL", name: "Newfoundland and Labrador" },
  { code: "NS", name: "Nova Scotia" },
  { code: "NT", name: "Northwest Territories" },
  { code: "NU", name: "Nunavut" },
  { code: "ON", name: "Ontario" },
  { code: "PE", name: "Prince Edward Island" },
  { code: "QC", name: "Quebec" },
  { code: "SK", name: "Saskatchewan" },
  { code: "YT", name: "Yukon" },
];

export const AU_STATES: readonly Subdivision[] = [
  { code: "ACT", name: "Australian Capital Territory" },
  { code: "NSW", name: "New South Wales" },
  { code: "NT", name: "Northern Territory" },
  { code: "QLD", name: "Queensland" },
  { code: "SA", name: "South Australia" },
  { code: "TAS", name: "Tasmania" },
  { code: "VIC", name: "Victoria" },
  { code: "WA", name: "Western Australia" },
];

/**
 * Subdivision lists keyed by ISO 3166-1 alpha-2 country code. Only the countries
 * with a curated dropdown are present; everything else uses free-text entry.
 */
export const SUBDIVISIONS: Readonly<Record<string, readonly Subdivision[]>> = {
  US: US_STATES,
  CA: CA_PROVINCES,
  AU: AU_STATES,
};

/**
 * The curated subdivision list for a country, or an empty array when the country
 * has no curated list (callers should then render a free-text state/province
 * input). Defaults to US when no country is provided.
 */
export function getSubdivisions(country?: string | null): readonly Subdivision[] {
  const code = (country || "US").toUpperCase();
  return SUBDIVISIONS[code] ?? [];
}

/** True when a country has a curated subdivision dropdown (vs free text). */
export function hasSubdivisions(country?: string | null): boolean {
  return getSubdivisions(country).length > 0;
}

/** URL-safe slug for a US state name, e.g. "Texas" -> "texas". */
export function stateNameToSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * Resolve a state URL slug (either a state-name slug like "texas" or a
 * two-letter code like "tx") to its canonical code + name, or null. Shared by
 * the web fuel-price pages and the api-server OG share-card route so the
 * slugging rules can never drift between them.
 */
export function usStateFromSlug(
  slug: string,
): { code: string; name: string } | null {
  const target = slug.toLowerCase();
  const found = US_STATES.find(
    (s) => stateNameToSlug(s.name) === target || s.code.toLowerCase() === target,
  );
  return found ? { code: found.code, name: found.name } : null;
}

/**
 * The full subdivision name for a code within a country (e.g. ("CA","US") ->
 * "California"). Falls back to the code itself when unknown.
 */
export function subdivisionName(
  code?: string | null,
  country?: string | null,
): string {
  if (!code) return "";
  const match = getSubdivisions(country).find(
    (s) => s.code.toUpperCase() === code.toUpperCase(),
  );
  return match?.name ?? code;
}
